import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';

// List of authorized admin emails - must match the list in SetupAdmin.js
const AUTHORIZED_ADMIN_EMAILS = [
  'topshotrewards@gmail.com'
  // You can add more emails if needed in the future
];

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.2rem;
  color: #666;
`;

const AccessDeniedContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  text-align: center;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const AccessDeniedTitle = styled.h2`
  color: #C41E3A;
  margin-bottom: 1rem;
`;

const AccessDeniedMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const AdminRoute = () => {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // First check if email is in the authorized list
        const isEmailAuthorized = AUTHORIZED_ADMIN_EMAILS.includes(currentUser.email.toLowerCase());
        
        if (!isEmailAuthorized) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        // Then check if user has admin flag in database
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().isAdmin) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  if (loading) {
    return <LoadingContainer>Checking permissions...</LoadingContainer>;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return (
      <AccessDeniedContainer>
        <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
        <AccessDeniedMessage>
          You don't have permission to access this area. 
          Admin access is restricted to authorized accounts only.
        </AccessDeniedMessage>
      </AccessDeniedContainer>
    );
  }

  return <Outlet />;
};

export default AdminRoute;
